import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Newspaper } from "lucide-react";
import {
  LayoutDashboard, Package, ShoppingBag, Users, Ticket, Sparkles, Globe, CreditCard, Settings,
  FolderTree, Boxes, RotateCcw, LayoutTemplate, UserRound,
} from "lucide-react";
import api from "@/lib/api";
import { ActiveStoreProvider, useActiveStore } from "@/context/ActiveStoreContext";
import StoreSwitcher from "@/components/app/StoreSwitcher";
import AddStoreModal from "@/components/onboarding/AddStoreModal";
import AppShell from "@/components/app/AppShell";
import { fmtDate, inr } from "@/components/app/ui";

// Grouped like the reference CRM sidebar: small section labels, icon + label rows.
const groups = [
  { label: "Overview", items: [
    { to: "/vendor", end: true, icon: LayoutDashboard, label: "Dashboard", tid: "vendor-sidebar-overview" },
  ] },
  { label: "Catalog", items: [
    { to: "/vendor/products", icon: Package, label: "Products", tid: "vendor-sidebar-products" },
    { to: "/vendor/categories", icon: FolderTree, label: "Categories", tid: "vendor-sidebar-categories" },
    { to: "/vendor/inventory", icon: Boxes, label: "Inventory", tid: "vendor-sidebar-inventory" },
  ] },
  { label: "Sales", items: [
    { to: "/vendor/orders", icon: ShoppingBag, label: "Orders", tid: "vendor-sidebar-orders" },
    { to: "/vendor/refunds", icon: RotateCcw, label: "Refunds", tid: "vendor-sidebar-refunds" },
    { to: "/vendor/customers", icon: Users, label: "Customers", tid: "vendor-sidebar-customers" },
    { to: "/vendor/coupons", icon: Ticket, label: "Coupons", tid: "vendor-sidebar-coupons" },
  ] },
  { label: "Store", items: [
    { to: "/vendor/homepage", icon: LayoutTemplate, label: "Setup", tid: "vendor-sidebar-homepage" },
    { to: "/vendor/blog", icon: Newspaper, label: "Blog", tid: "vendor-sidebar-blog" },
    { to: "/vendor/ai-theme", icon: Sparkles, label: "AI Theme", tid: "vendor-sidebar-ai-theme" },
    { to: "/vendor/domain", icon: Globe, label: "Custom Domain", tid: "vendor-sidebar-domain" },
    { to: "/vendor/plan", icon: CreditCard, label: "Plan & Billing", tid: "vendor-sidebar-plan" },
  ] },
  { label: "System", items: [
    { to: "/vendor/profile", icon: UserRound, label: "My Profile", tid: "vendor-sidebar-profile" },
    { to: "/vendor/settings", icon: Settings, label: "Settings", tid: "vendor-sidebar-settings" },
  ] },
];

export default function VendorLayout() {
  return <ActiveStoreProvider><VendorShell /></ActiveStoreProvider>;
}

function VendorShell() {
  const { activeId } = useActiveStore();
  const [store, setStore] = useState(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    setStore(null);                                      // never show the previous store's plan/billing while loading
    let live = true;
    const refresh = () => api.get("/vendor/store").then(({ data }) => { if (live) setStore(data); }).catch(() => {});
    refresh();
    window.addEventListener("store:refresh", refresh);   // fired by pages after billing/plan changes
    return () => { live = false; window.removeEventListener("store:refresh", refresh); };
  }, [activeId]);

  const b = store?.billing;
  const banner = b && (b.status === "lapsed" || b.outstanding?.count > 0) ? (
    <div data-testid="billing-banner" className={`px-4 sm:px-6 py-2.5 text-[13px] flex items-center gap-2.5 border-b ${b.status === "lapsed" ? "bg-red-50 border-red-200 text-red-800" : "bg-orange-50 border-orange-200 text-orange-800"}`}>
      {b.status === "lapsed" ? <AlertTriangle size={15} className="shrink-0" /> : <Clock size={15} className="shrink-0" />}
      <span className="flex-1">
        {b.status === "lapsed"
          ? <>Your account was moved to the <b>Free</b> plan because an invoice wasn&apos;t paid within {b.grace_days} days. Pay {inr(b.outstanding.amount)} to restore your <b>{b.subscribed_plan_name}</b> plan.</>
          : <>You have {b.outstanding.count} unpaid invoice{b.outstanding.count > 1 ? "s" : ""} ({inr(b.outstanding.amount)}). Pay by <b>{fmtDate(b.outstanding.next_due)}</b> to keep your plan.</>}
      </span>
      <Link to="/vendor/plan" className="font-semibold underline whitespace-nowrap">Pay now</Link>
    </div>
  ) : null;

  return (
    <>
    <AppShell
      banner={banner}
      groups={groups} homePath="/vendor" roleLabel="Store Owner" menuLinks={[{ to: "/vendor/profile", icon: UserRound, label: "My Profile" }, { to: "/vendor/settings", icon: Settings, label: "Settings" }]}
      logoutTestId="vendor-logout" userMenuTestId="vendor-user-menu" searchTestId="vendor-menu-search" toggleTestId="vendor-sidebar-toggle"
      sidebarFooter={store && (
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-orange-50 border border-blue-100 px-3 py-2.5">
          <div className="text-[11px] text-slate-500">Current plan</div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-neutral-900">{store.plan_info?.name || "Free"}</span>
            <Link to="/vendor/plan" className="text-[11px] font-semibold text-orange-600 hover:underline">Upgrade</Link>
          </div>
        </div>
      )}
      outletKey={activeId}
      topRight={<StoreSwitcher onAdd={() => setAdding(true)} />}
    />
    <AddStoreModal open={adding} onClose={() => setAdding(false)} />
    </>
  );
}
